/* eslint-disable lingui/text-restrictions */
/* eslint-disable lingui/no-unlocalized-strings */

import { t } from "@lingui/macro";
import {
  ArrowRight,
  ArrowUp,
  Check,
  CircleNotch,
  CreditCard,
  Crown,
  Fire,
  Lock,
  ShieldCheck,
  X,
} from "@phosphor-icons/react";
import { Button } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { getPlanDisplayName, type PlanTier, TIER_RANK } from "@reactive-resume/utils";
import { useState } from "react";

import { useToast } from "@/client/hooks/use-toast";
import { trackTikTokInitiateCheckout } from "@/client/libs/analytics";
import { axios } from "@/client/libs/axios";
import { useUser } from "@/client/services/user";
import { useBillingStore } from "@/client/stores/billing";

type Plan = {
  name: string;
  tier: NonNullable<PlanTier>;
  monthlyPrice: number;
  annualPrice: number;
  annualTotal: number;
  freeMonths: string;
  popular?: boolean;
  highlights: string[];
};

const plans: Plan[] = [
  {
    name: "Starter",
    tier: "starter",
    monthlyPrice: 9,
    annualPrice: 6,
    annualTotal: 69,
    freeMonths: "~4 months free",
    highlights: ["1 resume", "5 templates", "30 AI actions/day", "PDF export"],
  },
  {
    name: "Pro",
    tier: "pro",
    monthlyPrice: 19,
    annualPrice: 12,
    annualTotal: 139,
    freeMonths: "~4 months free",
    popular: true,
    highlights: [
      "3 resumes",
      "All 12+ templates",
      "150 AI actions/day",
      "ATS Score Checker",
      "Cover letters",
      "PDF + DOCX export",
    ],
  },
  {
    name: "Career Max",
    tier: "careerMax",
    monthlyPrice: 39,
    annualPrice: 22,
    annualTotal: 259,
    freeMonths: "~5 months free",
    highlights: [
      "Unlimited resumes",
      "Unlimited AI actions",
      "Auto-tailoring",
      "Unlimited cover letters",
      "AI chat assistant",
      "Priority processing",
    ],
  },
  {
    name: "Ultra",
    tier: "ultra",
    monthlyPrice: 79,
    annualPrice: 44,
    annualTotal: 529,
    freeMonths: "~5 months free",
    highlights: [
      "Everything in Career Max",
      "Fastest processing",
      "Bulk operations",
      "Early access features",
      "Priority support",
    ],
  },
];

const FEATURE_LABELS: Record<string, string> = {
  "ai-basic": "AI Writing",
  "ai-full": "Full AI Writing",
  "ai-chat": "AI Chat Assistant",
  "auto-tailor": "Auto-Tailoring",
  "ats-score": "ATS Score Checker",
  "job-match": "Job Description Matching",
  "cover-letter": "Cover Letters",
  "pdf-export": "PDF Export",
  "docx-export": "DOCX Export",
  "linkedin-import": "LinkedIn Import",
  "premium-templates": "Premium Templates",
  "all-fonts": "All Google Fonts",
  "unlimited-resumes": "Unlimited Resumes",
};

type UpgradePreview = {
  amountDue: number;
  currency: string;
  currentTier: string;
  newTier: string;
  currentPriceAmount: number | null;
  currentPriceInterval: string | null;
  newPriceAmount: number | null;
  newPriceInterval: string | null;
  periodEnd: number | null;
  paymentMethod: { brand: string; last4: string } | null;
};

const CARD_BRAND_LABELS: Record<string, string> = {
  visa: "Visa",
  mastercard: "Mastercard",
  amex: "American Express",
  discover: "Discover",
  diners: "Diners Club",
  jcb: "JCB",
  unionpay: "UnionPay",
};

const PlanActionButton = ({
  plan,
  userTier,
  annual,
  loading,
  onSubscribe,
  onUpgrade,
}: {
  plan: Plan;
  userTier: PlanTier;
  annual: boolean;
  loading: string | null;
  onSubscribe: (tier: string) => void;
  onUpgrade: (tier: string) => void;
}) => {
  const userRank = userTier ? TIER_RANK[userTier] ?? 0 : 0;
  const planRank = TIER_RANK[plan.tier] ?? 0;

  if (userTier && planRank === userRank) {
    return (
      <Button size="sm" className="mt-4 w-full" variant="ghost" disabled>
        <Crown size={14} weight="fill" className="mr-1" />
        Current Plan
      </Button>
    );
  }

  if (userTier && planRank < userRank) {
    return (
      <Button size="sm" className="mt-4 w-full" variant="ghost" disabled>
        Included
      </Button>
    );
  }

  if (userTier && planRank > userRank) {
    return (
      <Button
        size="sm"
        className="mt-4 w-full"
        variant={plan.popular ? "primary" : "outline"}
        disabled={loading !== null}
        onClick={() => onUpgrade(plan.tier)}
      >
        {loading === plan.tier ? (
          <CircleNotch size={14} className="animate-spin" />
        ) : (
          <>
            Upgrade <ArrowUp className="ml-1" weight="bold" size={14} />
          </>
        )}
      </Button>
    );
  }

  return (
    <Button
      size="sm"
      className="mt-4 w-full"
      variant={plan.popular ? "primary" : "outline"}
      disabled={loading !== null}
      onClick={() => onSubscribe(plan.tier)}
    >
      {loading === plan.tier ? (
        <CircleNotch size={14} className="animate-spin" />
      ) : (
        <>
          Start 3-day trial <ArrowRight className="ml-1" weight="bold" />
        </>
      )}
    </Button>
  );
};

const formatMoney = (amount: number | null, currency: string) => {
  if (amount === null) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(amount / 100);
};

const UpgradeConfirmation = ({
  preview,
  targetPlan,
  loading,
  acknowledged,
  onToggleAcknowledge,
  onConfirm,
  onBack,
}: {
  preview: UpgradePreview;
  targetPlan: Plan;
  loading: boolean;
  acknowledged: boolean;
  onToggleAcknowledge: () => void;
  onConfirm: () => void;
  onBack: () => void;
}) => {
  const prorationFormatted = formatMoney(preview.amountDue, preview.currency);
  const currentPlanName = getPlanDisplayName(preview.currentTier as PlanTier) ?? "Free";
  const newPlanName = getPlanDisplayName(preview.newTier as PlanTier) ?? targetPlan.name;
  const currentPriceFormatted = formatMoney(preview.currentPriceAmount, preview.currency);
  const newPriceFormatted = formatMoney(preview.newPriceAmount, preview.currency);
  const currentInterval = preview.currentPriceInterval === "year" ? "/yr" : "/mo";
  const newInterval = preview.newPriceInterval === "year" ? "/yr" : "/mo";

  const periodEndFormatted = preview.periodEnd
    ? new Date(preview.periodEnd).toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;

  const cardBrand = preview.paymentMethod
    ? CARD_BRAND_LABELS[preview.paymentMethod.brand] ?? preview.paymentMethod.brand
    : null;

  return (
    <div className="mx-auto max-w-lg space-y-5 py-2">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-xl font-bold">Confirm your upgrade</h2>
        <p className="mt-1 text-sm text-foreground/50">
          Review the changes below before confirming.
        </p>
      </div>

      {/* Plan comparison */}
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4">
        <div className="rounded-lg border border-border/50 bg-secondary/20 p-4 text-center">
          <p className="text-[10px] font-medium uppercase tracking-wider text-foreground/40">
            Current plan
          </p>
          <p className="mt-1 text-lg font-semibold">{currentPlanName}</p>
          <p className="text-sm text-foreground/50">
            {currentPriceFormatted}
            <span className="text-xs">{currentInterval}</span>
          </p>
        </div>

        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10">
          <ArrowRight size={16} className="text-primary" weight="bold" />
        </div>

        <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 text-center">
          <p className="text-[10px] font-medium uppercase tracking-wider text-primary/70">
            New plan
          </p>
          <p className="mt-1 text-lg font-semibold text-primary">{newPlanName}</p>
          <p className="text-sm text-foreground/50">
            {newPriceFormatted}
            <span className="text-xs">{newInterval}</span>
          </p>
        </div>
      </div>

      {/* Charge breakdown */}
      <div className="rounded-lg border bg-secondary/30 p-4">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-foreground/40">
          Charge summary
        </h4>
        <div className="mt-3 space-y-2.5">
          <div className="flex items-center justify-between text-sm">
            <span className="text-foreground/60">Prorated amount due today</span>
            <span className="text-lg font-bold">{prorationFormatted}</span>
          </div>
          {periodEndFormatted && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-foreground/60">Next regular billing date</span>
              <span className="font-medium">{periodEndFormatted}</span>
            </div>
          )}
          <div className="flex items-center justify-between text-sm">
            <span className="text-foreground/60">New recurring price</span>
            <span className="font-medium">
              {newPriceFormatted}
              <span className="text-xs text-foreground/40">{newInterval}</span>
            </span>
          </div>
        </div>
      </div>

      {/* Payment method */}
      {preview.paymentMethod && (
        <div className="flex items-center gap-3 rounded-lg border px-4 py-3">
          <CreditCard size={20} className="shrink-0 text-foreground/40" />
          <div className="flex-1 text-sm">
            <span className="font-medium">{cardBrand}</span>{" "}
            <span className="text-foreground/50">ending in</span>{" "}
            <span className="font-mono font-medium">{preview.paymentMethod.last4}</span>
          </div>
          <Lock size={14} className="shrink-0 text-foreground/30" />
        </div>
      )}

      {/* Acknowledgment checkbox */}
      <label className="flex cursor-pointer items-start gap-3 rounded-lg border px-4 py-3 transition-colors hover:bg-secondary/20">
        <input
          type="checkbox"
          checked={acknowledged}
          onChange={onToggleAcknowledge}
          className="mt-0.5 h-4 w-4 shrink-0 rounded border-foreground/30 accent-primary"
        />
        <span className="text-sm text-foreground/70">
          I confirm upgrading to <strong>{newPlanName}</strong> and authorize a charge of{" "}
          <strong>{prorationFormatted}</strong> to my payment method on file.
        </span>
      </label>

      {/* Actions */}
      <div className="flex gap-3">
        <Button variant="outline" className="flex-1" onClick={onBack} disabled={loading}>
          Back
        </Button>
        <Button
          variant="primary"
          className="flex-1 gap-x-2"
          onClick={onConfirm}
          disabled={loading || !acknowledged}
        >
          {loading ? (
            <CircleNotch size={16} className="animate-spin" />
          ) : (
            <>
              Pay {prorationFormatted} & Upgrade
              <ArrowUp size={14} weight="bold" />
            </>
          )}
        </Button>
      </div>

      {/* Trust badge */}
      <div className="flex items-center justify-center gap-1.5 text-[11px] text-foreground/30">
        <ShieldCheck size={14} weight="fill" />
        Payments processed securely by Stripe
      </div>
    </div>
  );
};

export const PaywallModal = () => {
  const { paywallOpen, paywallFeature, paywallMessage, closePaywall } = useBillingStore();
  const { user } = useUser();
  const { toast } = useToast();
  const [annual, setAnnual] = useState(false);
  const [loading, setLoading] = useState<string | null>(null);
  const [upgradePreview, setUpgradePreview] = useState<UpgradePreview | null>(null);
  const [upgradeTarget, setUpgradeTarget] = useState<Plan | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  if (!paywallOpen) return null;

  const userTier = (user?.planTier ?? null) as PlanTier;
  const isSubscribed = !!userTier;
  const featureLabel = paywallFeature ? FEATURE_LABELS[paywallFeature] ?? paywallFeature : null;

  const handleSubscribe = async (tier: string) => {
    setLoading(tier);
    try {
      const interval = annual ? "annual" : "monthly";
      const selectedPlan = plans.find((plan) => plan.tier === tier);
      if (!selectedPlan) throw new Error("Unknown plan");

      const { data } = await axios.post<{ url: string; sessionId: string }>("/billing/checkout", {
        tier,
        interval,
        sourceUrl: window.location.href,
      });
      await trackTikTokInitiateCheckout({
        tier: selectedPlan.tier,
        interval,
        eventId: data.sessionId,
        userId: user?.id ?? null,
        email: user?.email,
      });
      if (data.url) window.location.href = data.url;
    } catch {
      toast({
        variant: "error",
        title: t`Checkout failed`,
        description: t`Something went wrong. Please try again.`,
      });
      setLoading(null);
    }
  };

  const handleUpgrade = async (tier: string) => {
    setLoading(tier);
    const target = plans.find((p) => p.tier === tier);
    try {
      const interval = annual ? "annual" : "monthly";
      const { data } = await axios.post<UpgradePreview>("/billing/upgrade-preview", {
        tier,
        interval,
      });
      setUpgradePreview(data);
      setUpgradeTarget(target ?? null);
    } catch {
      toast({
        variant: "error",
        title: t`Failed to load upgrade details`,
        description: t`Please try again.`,
      });
    } finally {
      setLoading(null);
    }
  };

  const handleConfirmUpgrade = async () => {
    if (!upgradeTarget) return;
    setConfirmLoading(true);
    try {
      const interval = annual ? "annual" : "monthly";
      await axios.post("/billing/upgrade", { tier: upgradeTarget.tier, interval });
      window.location.reload();
    } catch {
      toast({
        variant: "error",
        title: t`Upgrade failed`,
        description: t`Something went wrong processing your upgrade. Please try again or contact support.`,
      });
      setConfirmLoading(false);
    }
  };

  const handleClose = () => {
    setUpgradePreview(null);
    setUpgradeTarget(null);
    setConfirmLoading(false);
    setAcknowledged(false);
    setLoading(null);
    closePaywall();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleClose} />

      <div className="relative z-10 mx-4 max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-2xl bg-background p-6 shadow-2xl sm:p-8">
        <button
          onClick={handleClose}
          className="absolute right-4 top-4 rounded-full p-1 text-foreground/40 transition-colors hover:text-foreground"
        >
          <X size={20} />
        </button>

        {upgradePreview && upgradeTarget ? (
          <UpgradeConfirmation
            preview={upgradePreview}
            targetPlan={upgradeTarget}
            loading={confirmLoading}
            acknowledged={acknowledged}
            onToggleAcknowledge={() => setAcknowledged((v) => !v)}
            onConfirm={handleConfirmUpgrade}
            onBack={() => {
              setUpgradePreview(null);
              setUpgradeTarget(null);
              setAcknowledged(false);
            }}
          />
        ) : (
          <>
            <div className="text-center">
              {paywallMessage && (
                <p className="mx-auto max-w-md rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-300">
                  {paywallMessage}
                </p>
              )}
              {!paywallMessage && featureLabel && (
                <p className="text-sm text-foreground/50">
                  {t`To use`} <span className="font-medium text-foreground">{featureLabel}</span>
                  {t`, choose a plan:`}
                </p>
              )}
              <h2 className="mt-2 text-2xl font-bold">
                {isSubscribed ? t`Upgrade your plan` : t`Choose a plan`}
              </h2>
              {!isSubscribed && (
                <p className="mt-1 text-sm text-foreground/50">
                  {t`3-day free trial. Card required. Cancel anytime before the trial ends.`}
                </p>
              )}
              {isSubscribed && (
                <p className="mt-1 text-sm text-foreground/50">
                  You'll only pay the prorated difference for the rest of your billing period.
                </p>
              )}

              <div className="mt-4 flex items-center justify-center gap-3">
                <button
                  onClick={() => setAnnual(false)}
                  className={cn(
                    "rounded-full px-4 py-1.5 text-xs font-medium transition-colors",
                    !annual ? "bg-primary text-primary-foreground" : "text-foreground/60",
                  )}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setAnnual(true)}
                  className={cn(
                    "flex items-center gap-1 rounded-full px-4 py-1.5 text-xs font-medium transition-colors",
                    annual ? "bg-primary text-primary-foreground" : "text-foreground/60",
                  )}
                >
                  <Fire weight="fill" className="h-3 w-3" />
                  Yearly: save up to 5 months
                </button>
              </div>
            </div>

            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {plans.map((plan) => (
                <div
                  key={plan.tier}
                  className={cn(
                    "relative flex flex-col rounded-xl border p-5",
                    plan.popular
                      ? "border-primary shadow-md shadow-primary/10"
                      : "border-border/50",
                    userTier &&
                      (TIER_RANK[plan.tier] ?? 0) < (TIER_RANK[userTier] ?? 0) &&
                      "opacity-40",
                  )}
                >
                  {plan.popular && !isSubscribed && (
                    <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-0.5 text-[10px] font-semibold text-primary-foreground">
                      Most popular
                    </div>
                  )}
                  {userTier && plan.tier === userTier && (
                    <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 rounded-full bg-secondary px-3 py-0.5 text-[10px] font-semibold text-foreground/70">
                      Current
                    </div>
                  )}

                  <h3 className="text-sm font-semibold">{plan.name}</h3>

                  <div className="mt-3 flex items-baseline gap-1">
                    <span className="text-3xl font-bold">
                      ${annual ? plan.annualPrice : plan.monthlyPrice}
                    </span>
                    <span className="text-xs text-foreground/50">/mo</span>
                  </div>

                  {annual && (
                    <p className="mt-1 text-[10px] text-foreground/40">
                      ${plan.annualTotal}/yr &middot;{" "}
                      <span className="text-green-600">{plan.freeMonths}</span>
                    </p>
                  )}

                  <PlanActionButton
                    plan={plan}
                    userTier={userTier}
                    annual={annual}
                    loading={loading}
                    onSubscribe={handleSubscribe}
                    onUpgrade={handleUpgrade}
                  />

                  <ul className="mt-4 space-y-2 border-t border-border/30 pt-4">
                    {plan.highlights.map((h, i) => (
                      <li key={i} className="flex items-start gap-1.5 text-xs text-foreground/60">
                        <Check weight="bold" className="mt-0.5 h-3 w-3 shrink-0 text-green-500" />
                        {h}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
